/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { useMemo, type FC } from 'react'
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

type WeekDay = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'

export type WeekOptions = {
  value: WeekDay
  label: string
}

const days: WeekOptions[] = [
  { value: 'sunday', label: 'Domingo' },
  { value: 'monday', label: 'Segunda-feira' },
  { value: 'tuesday', label: 'Terça-feira' },
  { value: 'wednesday', label: 'Quarta-feira' },
  { value: 'thursday', label: 'Quinta-feira' },
  { value: 'friday', label: 'Sexta-feira' },
  { value: 'saturday', label: 'Sábado' }
]

const validationSchema = yup.object().shape({
  latitude: yup.number().nullable().transform((_, val) => val === Number(val) ? val : null),
  longitude: yup.number().nullable().transform((_, val) => val === Number(val) ? val : null),
  store: yup.string().nullable(),
  rhBranchId: yup.string().matches(/^\d+(?:-\d+)*$/, 'O Código da filial no sistema de RH é inválido').nullable(),
  name: yup.string().required('O nome é obrigatório').default('').min(0).max(255),
  address: yup.string().required('O endereço é obrigatório').default('').min(0).max(255),
  phone: yup.string().required('O telefone é obrigatório').default('').min(0).max(255),
  neighborhood: yup.string().required('O bairro é obrigatório').default('').min(0).max(255),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('active'),
  customTheme: yup.object().nullable().shape({
    backgroundColor: yup.string().trim(),
    textColor: yup.string().trim(),
  }),
  region: yup.string()
    .oneOf(['north_zone', 'south_zone', 'east_zone', 'west_zone', 'center', 'niteroi'])
    .required('A região é obrigatória')
    .default('north_zone'),
  businessHours: yup.object().nullable().notRequired().shape({
    sunday: yup.object().shape({
      opensAt: yup.string().nullable().notRequired(),
      closesAt: yup.string().nullable().notRequired(),
    }),
    monday: yup.object().shape({
      opensAt: yup.string().nullable().notRequired(),
      closesAt: yup.string().nullable().notRequired(),
    }),
    tuesday: yup.object().shape({
      opensAt: yup.string().nullable().notRequired(),
      closesAt: yup.string().nullable().notRequired(),
    }),
    wednesday: yup.object().shape({
      opensAt: yup.string().nullable().notRequired(),
      closesAt: yup.string().nullable().notRequired(),
    }),
    thursday: yup.object().shape({
      opensAt: yup.string().nullable().notRequired(),
      closesAt: yup.string().nullable().notRequired(),
    }),
    friday: yup.object().shape({
      opensAt: yup.string().nullable().notRequired(),
      closesAt: yup.string().nullable().notRequired(),
    }),
    saturday: yup.object().shape({
      opensAt: yup.string().nullable().notRequired(),
      closesAt: yup.string().nullable().notRequired(),
    }),
  })
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormStoreHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormStoreHandleSubmit,
  initialValues?: ValidationSchema,
  isLoading?: boolean
}

/**
 * FormStore is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormStore from './src/views/PostEdit/FormStore.tsx'
 *
 * const MyComponent = () => (
 *   <FormStore />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormStore: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type='text'
            placeholder='Informe o nome da loja'
            name='name'
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={3} className="mb-3">
          <Form.Label>Código da filial no sistema de RH</Form.Label>
          <Form.Control
            type='number'
            placeholder='Informe o código da filial'
            name='rhBranchId'
            value={formik?.values?.rhBranchId ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Cor de fundo</Form.Label>
          <Form.Control
            type='color'
            name='customTheme.backgroundColor'
            value={formik.values.customTheme?.backgroundColor}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Cor dos textos</Form.Label>
          <Form.Control
            type='color'
            placeholder='Informe o nome da loja'
            name='customTheme.textColor'
            value={formik.values.customTheme?.textColor}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Código de referência da loja</Form.Label>
          <Form.Control
            type='text'
            placeholder='EX: BARRA_01'
            name='store'
            value={formik?.values?.store ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Telefone</Form.Label>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="Informe o telefone da loja"
              name='phone'
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </InputGroup>
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Situação</Form.Label>
          <Form.Select
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='active'>🌎 Pública</option>
            <option value='inactive'>🔒 Privada</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={5} className="mb-3">
          <Form.Label>Endereço</Form.Label>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="Informe o endereço da loja"
              name='address'
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </InputGroup>
        </Form.Group>
        <Form.Group as={Col} md={5} className="mb-3">
          <Form.Label>Bairro</Form.Label>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="Informe o bairro da loja"
              name='neighborhood'
              value={formik.values.neighborhood}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </InputGroup>
        </Form.Group>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Região</Form.Label>
          <Form.Select
            value={formik.values.region}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="region"
          >
            <option selected value=''>Selecione uma opção</option>
            <option value='north_zone'>Zona Norte</option>
            <option value='south_zone'>Zona Sul</option>
            <option value='east_zone'>Zona Leste</option>
            <option value='west_zone'>Zona Oeste</option>
            <option value='center'>Centro</option>
            <option value='niteroi'>Niterói</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label>Horário de Funcionamento</Form.Label>
          {days.map((day, index) => (
            <Col md={4} key={`${day.label}${index}`}>
              <InputGroup className="mb-3">
                <InputGroup.Text id="basic-addon1" style={{ width: 130 }}>
                  {day.label}
                </InputGroup.Text>
                <Form.Control
                  type="time"
                  placeholder="Abre às 08:00"
                  name={`businessHours.${day.value}.opensAt`}
                  value={formik.values.businessHours ? formik.values.businessHours[day.value].opensAt as string : undefined}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || isLoading}
                />
                <Form.Control
                  type="time"
                  placeholder="Fecha às 22:00"
                  name={`businessHours.${day.value}.closesAt`}
                  value={formik.values.businessHours ? formik.values.businessHours[day.value].closesAt as string : undefined}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || isLoading}
                />
              </InputGroup>
            </Col>
          ))}
        </Form.Group>
        <Form.Group as={Col} md={6} className="mb-3">
          <Form.Label>Latitude</Form.Label>
          <InputGroup className="mb-3">
            <Form.Control
              type="number"
              placeholder="Informe a latitude"
              name='latitude'
              value={formik?.values?.latitude ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </InputGroup>
        </Form.Group>
        <Form.Group as={Col} md={6} className="mb-3">
          <Form.Label>Longitude</Form.Label>
          <InputGroup className="mb-3">
            <Form.Control
              type="number"
              placeholder="Informe a longitude"
              name='longitude'
              value={formik?.values?.longitude ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </InputGroup>
        </Form.Group>
        <Col md={12}>
          <Button
            variant='link'
            style={{ marginRight: 10 }}
            onClick={() => navigate('/stores')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type='submit'
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
        </Col>
        <Col md={12} style={{ paddingTop: 10 }}>
          {formik?.errors && Object.keys(formik.errors).length > 0 && (
            <>
              {Object.values(formik.errors).map((error, index) => (
                <Alert variant='warning' key={index}>
                  {error}
                </Alert>
              ))}
            </>
          )}
        </Col>
      </Row>
    </Form>
  )
}

export default FormStore
